<template>
  <div class="operations-report-dialog">
    <custom-dialog
      width="600px"
      :title="model.title"
      :visible="visible"
      v-on="$listeners"
      :append-to-body="true"
    >
      <div class="dialog-body">
        <el-form
          ref="form"
          :model="form"
          :rules="rules"
          label-position="top"
        >
          <el-form-item :label="$t('operations_report_dialog.shu_ju_zhou_qi')" prop="reportType">
            <el-row :gutter="12">
              <el-col :span="6" class="period-picker-col">
                <custom-select
                  v-model="form.reportType"
                  code="report_export_type"
                  @change="onReportTypeChange"
                ></custom-select>
              </el-col>
              <el-col :span="18" class="period-picker-col">
                <custom-date-picker
                  v-show="+form.reportType === 1"
                  v-model="dateTimes"
                  :init-value="dateInitValue"
                  type="daterange"
                  :picker-options="dayRangePickerOptions"
                  value-format="YYYY-MM-DD HH:mm:ss"
                  :start-placeholder="$t('Common.com_start_date')"
                  :end-placeholder="$t('Common.com_end_date')"
                  :clearable="false"
                  style="width: 100%"
                ></custom-date-picker>
                <week-picker
                  v-show="+form.reportType === 2"
                  v-model="reportWeekTime"
                  :disableFuture="true"
                  @change="onReportWeekTimeChange"
                  class="report-week-picker"
                  style="width: 100%"
                ></week-picker>
                <custom-date-picker
                  v-show="+form.reportType === 3"
                  v-model="reportMonthTime"
                  :init-value="monthInitValue"
                  type="monthrange"
                  :picker-options="monthRangePickerOptions"
                  value-format="yyyy-MM"
                  format="yyyy-MM"
                  range-separator="—"
                  :start-placeholder="$t('Common.com_start_month')"
                  :end-placeholder="$t('Common.com_end_month')"
                  :clearable="false"
                  style="width: 100%"
                ></custom-date-picker>
              </el-col>
            </el-row>
          </el-form-item>
          <el-form-item :label="$t('Common.com_email')" prop="email">
            <el-input
              v-model.trim="form.email"
              :placeholder="$t('operations_report_dialog.srdgyxqygkzdsr5gyx')"
            ></el-input>
          </el-form-item>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button type="info" plain @click="closeDialog">{{ $t('Common.com_cancel') }}</el-button>
        <el-button type="warning" :loading="submitting" :disabled="submitting" @click="confirm">{{ $t('Common.com_confirm') }}</el-button>
      </div>
    </custom-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import apis from '@/utils/apis';
import dialogMixins from '@/mixins/dialog_mixins.js';
import weekPicker from '@/components/weekRangePicker.vue';

export default {
  mixins: [dialogMixins],
  components: {
    weekPicker,
  },
  data() {
    return {
      submitting: false,
      dayRangeAnchorDate: null,
      monthRangeAnchorDate: null,
      form: {
        userOrgType: 'CS',
        userOrgId: '',
        userOrgName: '',
        userId: '',
        email: '',
        reportType: 1,
        reportStartDate: '',
        reportEndDate: '',
      },
      dateTimes: [
        this.$momentWorld(moment(this.$getLocalTime()).subtract(14, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss')),
        this.$momentWorld(moment(this.$getLocalTime()).subtract(1, 'days').endOf('day').format('YYYY-MM-DD HH:mm:ss')),
      ],
      reportWeekTime: [
        this.$momentWorld(moment().subtract(11, 'weeks')),
        this.$momentWorld(moment()),
      ],
      lastReportWeekTime: [],
      reportMonthTime: [
        this.$momentWorld(moment(this.$getLocalTime()).subtract(11, 'months').format('YYYY-MM')),
        this.$momentWorld(moment(this.$getLocalTime()).format('YYYY-MM')),
      ],
      dayRangePickerOptions: {
        firstDayOfWeek: 1,
        onPick: ({ minDate, maxDate }) => {
          this.dayRangeAnchorDate = maxDate ? null : minDate;
        },
        disabledDate: (time) => {
          const minYearLimit = moment(this.$momentElLocal(moment('2016-01-01 00:00:00'))).valueOf();
          const todayStart = moment(this.$getLocalTime()).startOf('day').valueOf();
          if (time.getTime() < minYearLimit) {
            return true;
          }
          if (time.getTime() >= todayStart) {
            return true;
          }
          if (!this.dayRangeAnchorDate) {
            return false;
          }
          const anchor = moment(this.dayRangeAnchorDate).startOf('day');
          const current = moment(time).startOf('day');
          // 和提交时的区间校验保持一致，按首尾都包含来计算天数
          const dayCount = Math.abs(current.diff(anchor, 'days')) + 1;
          return dayCount !== 14;
        },
      },
      monthRangePickerOptions: {
        onPick: ({ minDate, maxDate }) => {
          this.monthRangeAnchorDate = maxDate ? null : minDate;
        },
        disabledDate: (time) => {
          const minYearLimit = moment('2016-01-01 00:00:00').valueOf();
          if (time.getTime() < minYearLimit) {
            return true;
          }
          if (time.getTime() > moment(this.$getLocalTime()).valueOf()) {
            return true;
          }
          if (!this.monthRangeAnchorDate) {
            return false;
          }
          const monthCount = Math.abs(
            moment(time).startOf('month').diff(moment(this.monthRangeAnchorDate).startOf('month'), 'months'),
          ) + 1;
          return monthCount !== 12;
        },
      },
    };
  },
  computed: {
    dateInitValue() {
      if (!this.dateTimes || !this.dateTimes.length) {
        return [];
      }
      return this.dateTimes.map(time => this.$momentElLocal(time));
    },
    monthInitValue() {
      // 月报默认选择当前月及往前 11 个月，共 12 个月
      return [
        moment(this.$getLocalTime()).subtract(11, 'months').format('YYYY-MM'),
        moment(this.$getLocalTime()).format('YYYY-MM'),
      ];
    },
    rules() {
      return {
        reportType: [{ required: true, message: this.$t('operations_report_dialog.qxzsjzq'), trigger: 'change' }],
        email: [
          { required: true, message: this.$t('operations_report_dialog.qsryx'), trigger: 'blur' },
          { validator: this.validateEmail, trigger: 'blur' },
        ],
      };
    },
  },
  mounted() {
    this.init();
  },
  watch: {},
  methods: {
    init: function () {
      this.setCurrentUserInfo();
      if (this.model && this.model.data) {
        const data = this.model.data;
        this.form.email = data.email || '';
      }
      this.normalizeReportWeekTime('end');
      this.syncPeriodValue();
    },
    closeDialog: function () {
      this.visible = false;
      setTimeout(() => {
        this.$emit('close', false);
      }, 250);
    },

    onReportTypeChange: function () {
      if (+this.form.reportType === 2) {
        this.normalizeReportWeekTime('end');
      }
      this.syncPeriodValue();
    },
    onReportWeekTimeChange: function (val) {
      if (!val || val.length < 2) {
        return;
      }
      const changeInfo = this.getReportWeekChangeInfo(val);
      this.setReportWeekTimeByAnchor(changeInfo.type, changeInfo.anchor);
      this.syncPeriodValue();
    },
    setCurrentUserInfo: function () {
      const userInfo = this.userInfo || {};
      this.form.userOrgType = userInfo.bizType || this.form.userOrgType;
      this.form.userOrgId = userInfo.rootOrgId || this.form.userOrgId;
      this.form.userOrgName = userInfo.rootOrgName || this.form.userOrgName;
      this.form.userId = userInfo.userId || this.form.userId;
    },
    updateReportWeekLocale: function () {
      moment.updateLocale('en', {
        week: {
          dow: 1,
          doy: 7,
        },
      });
    },
    getReportWeekKey: function (time) {
      this.updateReportWeekLocale();
      return time ? moment(time).startOf('week').format('YYYY-MM-DD') : '';
    },
    getReportWeekChangeInfo: function (val) {
      const oldStart = this.getReportWeekKey(this.lastReportWeekTime[0]);
      const oldEnd = this.getReportWeekKey(this.lastReportWeekTime[1]);
      const newStart = this.getReportWeekKey(val[0]);
      const newEnd = this.getReportWeekKey(val[1]);
      if (newStart !== oldStart && newEnd === oldEnd) {
        return { type: 'start', anchor: val[0] };
      }
      if (newEnd !== oldEnd && newStart === oldStart) {
        return { type: 'end', anchor: val[1] };
      }
      // 周选择器会自动交换开始/结束，这里还原用户刚选择的那一端。
      if (newStart === oldEnd && newEnd !== oldEnd) {
        return { type: 'start', anchor: val[1] };
      }
      if (newEnd === oldStart && newStart !== oldStart) {
        return { type: 'end', anchor: val[0] };
      }
      return newEnd !== oldEnd ? { type: 'end', anchor: val[1] } : { type: 'start', anchor: val[0] };
    },
    getFixedReportWeekRange: function (time, changeType) {
      this.updateReportWeekLocale();
      let anchor = moment(time).startOf('week');
      const maxWeek = moment(this.$getLocalTime()).startOf('week');
      if (!anchor.isValid()) {
        anchor = maxWeek;
      }
      if (anchor.isAfter(maxWeek)) {
        anchor = maxWeek;
      }
      if (changeType === 'end') {
        return [
          anchor.clone().subtract(11, 'weeks'),
          anchor.clone(),
        ];
      }
      const end = anchor.clone().add(11, 'weeks');
      if (end.isAfter(maxWeek)) {
        return [
          anchor.clone().subtract(11, 'weeks'),
          anchor.clone(),
        ];
      }
      return [
        anchor.clone(),
        end,
      ];
    },
    normalizeReportWeekTime: function (changeType, val) {
      const weekTime = val || this.reportWeekTime;
      const anchor = changeType === 'start' ? weekTime[0] : weekTime[1];
      this.setReportWeekTimeByAnchor(changeType, anchor);
    },
    setReportWeekTimeByAnchor: function (changeType, anchor) {
      const range = this.getFixedReportWeekRange(anchor, changeType);
      this.reportWeekTime = range;
      this.lastReportWeekTime = range.slice();
    },
    syncPeriodValue: function () {
      if (+this.form.reportType === 2) {
        this.updateReportWeekLocale();
        this.form.reportStartDate = this.reportWeekTime.length ? moment(this.reportWeekTime[0]).format('ggggww') : '';
        this.form.reportEndDate = this.reportWeekTime.length ? moment(this.reportWeekTime[1]).format('ggggww') : '';
      } else if (+this.form.reportType === 3) {
        this.form.reportStartDate = this.reportMonthTime && this.reportMonthTime.length ? this.reportMonthTime[0] : '';
        this.form.reportEndDate = this.reportMonthTime && this.reportMonthTime.length ? this.reportMonthTime[1] : '';

      } else {
        this.form.reportStartDate = this.dateTimes && this.dateTimes.length ? this.dateTimes[0] : '';
        this.form.reportEndDate = this.dateTimes && this.dateTimes.length ? this.dateTimes[1] : '';
      }

    },
    validateEmail: function (rule, value, callback) {
      const emailList = (value || '')
        .split(';')
        .map(item => item.trim())
        .filter(item => item);
      const emailReg = /^\w+([-.+]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
      if (!emailList.length) {
        callback(new Error(this.$t('operations_report_dialog.qsryx')));
        return;
      }
      if (emailList.length > 5) {
        callback(new Error(this.$t('operations_report_dialog.zdsr5gyx')));
        return;
      }
      const invalidEmail = emailList.find(item => !emailReg.test(item));
      if (invalidEmail) {
        callback(new Error(this.$t('operations_report_dialog.qsrzqdyx')));
        return;
      }
      callback();
    },
    validateReportRange: function () {
      if (+this.form.reportType === 1) {
        if (!this.dateTimes || this.dateTimes.length < 2) {
          return false;
        }
        const start = moment(this.$momentElLocal(this.dateTimes[0]));
        const end = moment(this.$momentElLocal(this.dateTimes[1]));
        const dayCount = end.startOf('day').diff(start.startOf('day'), 'days') + 1;
        if (dayCount !== 14) {
          this.$message.error(this.$t('operations_report_dialog.rbsjqjjzc14t'));
          return false;
        }
        return true;
      }
      if (+this.form.reportType === 2) {
        if (!this.reportWeekTime || this.reportWeekTime.length < 2) {
          return false;
        }
        this.updateReportWeekLocale();
        const start = moment(this.reportWeekTime[0]).startOf('week');
        const end = moment(this.reportWeekTime[1]).startOf('week');
        const maxWeek = moment(this.$getLocalTime()).startOf('week');
        const weekCount = end.diff(start, 'weeks') + 1;
        if (end.isAfter(maxWeek)) {
          this.$message.error(this.$t('operations_report_dialog.zbjssjbnxzwlz'));
          return false;
        }
        if (weekCount !== 12) {
          this.$message.error(this.$t('operations_report_dialog.zbsjqjjzc12z'));
          return false;
        }
        return true;
      }
      if (+this.form.reportType === 3) {
        if (!this.reportMonthTime || this.reportMonthTime.length < 2) {
          return false;
        }
        const start = moment(this.reportMonthTime[0], 'YYYY-MM').startOf('month');
        const end = moment(this.reportMonthTime[1], 'YYYY-MM').startOf('month');
        const monthCount = end.diff(start, 'months') + 1;
        if (monthCount !== 12) {
          this.$message.error(this.$t('operations_report_dialog.ybsjqjjzc12gy'));
          return false;
        }
        return true;
      }
      return true;
    },
    getSubmitData: function () {
      this.syncPeriodValue();
      this.setCurrentUserInfo();
      const emailList = this.form.email
        .split(';')
        .map(item => item.trim())
        .filter(item => item);
      let reportStartDate = this.form.reportStartDate;
      let reportEndDate = this.form.reportEndDate;
      if (+this.form.reportType === 1) {
        const localStartDate = reportStartDate ? this.$momentElLocal(reportStartDate, 'YYYY-MM-DD') : '';
        const localEndDate = reportEndDate ? this.$momentElLocal(reportEndDate, 'YYYY-MM-DD') : '';
        reportStartDate = localStartDate ? moment(localStartDate, 'YYYY-MM-DD').format('YYYYMMDD') : '';
        reportEndDate = localEndDate ? moment(localEndDate, 'YYYY-MM-DD').format('YYYYMMDD') : '';
      }
      if (+this.form.reportType === 2) {
        // 直接使用不用修改值
      }
      if (+this.form.reportType === 3) {
        const localStartDate = reportStartDate ? this.$momentElLocal(reportStartDate, 'YYYY-MM') : '';
        const localEndDate = reportEndDate ? this.$momentElLocal(reportEndDate, 'YYYY-MM') : '';
        reportStartDate = localStartDate ? moment(localStartDate, 'YYYY-MM').format('YYYYMM') : '';
        reportEndDate = localEndDate ? moment(localEndDate, 'YYYY-MM').format('YYYYMM') : '';
      }
      return {
        userOrgType: this.form.userOrgType,
        userOrgId: this.form.userOrgId,
        userOrgName: this.form.userOrgName,
        userId: this.form.userId,
        reportType: this.form.reportType,
        reportStartDate,
        reportEndDate,
        email: this.form.email,
        emailList,
      };
    },
    confirm: function () {
      // 提交过程中禁止重复触发，避免生成多条相同任务
      if (this.submitting) {
        return;
      }
      this.submitting = true;
      this.syncPeriodValue();
      this.$refs.form.validate((valid) => {
        if (!valid) {
          this.submitting = false;
          return;
        }
        if (!this.validateReportRange()) {
          this.submitting = false;
          return;
        }
        this.$getJSON(apis.workReportExport2Oss, this.getSubmitData(), true).then(() => {
          this.$message({
            message: this.$t('Common.com_handle_success'),
            type: 'success',
          });
          this.$emit('success');
          this.closeDialog();
        }).finally(() => {
          this.submitting = false;
        });
      });
    },
  },
};
</script>

<style lang="scss">
.operations-report-dialog {
  .period-picker-col {
    padding: 0 !important;
  }
}

.report-week-picker {
  width: 100%;
  line-height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .el-date-editor {
    flex: 1;
    min-width: 0;
    width: auto !important;
  }

  .bar {
    flex: 0 0 28px;
    margin: 0;
  }

  .el-input__suffix {
    top: 0;
    right: 0;
    width: 25px;
    height: 40px;
    line-height: 40px;
    float: none;
    display: block;
    overflow: hidden;

    .el-input__suffix-inner,
    .el-input__icon {
      width: 25px;
      height: 40px;
      line-height: 40px;
      display: inline;
    }

    .el-input__icon {
      color: #FFAD00;

      &:before {
        display: none;
      }

      &:after {
        height: 40px;
        line-height: 40px;
        display: inline !important;
      }
    }
  }
}
</style>
